/* eslint-disable react/jsx-max-depth */
import { useRef } from "react";

import classNames from "classnames";
import { List } from "react-virtualized";

import { colorsPalettes } from "@similarweb/styles";
import { AutosizeInput, Button, FlexRow, ListContentLoader, PlainTooltip } from "@similarweb/ui-components";

import { useTopicsContext } from "app/pages/activation/context/Topics/TopicsContext";
import { useTopicsModalContext } from "app/pages/activation/context/Topics/TopicsModalContext";
import { ActivationComponentAssets } from "app/pages/activation/helpers/ActivationAssets";
import { useCreateFlows } from "app/pages/activation/hooks/topics/modal/useCreateFlows";
import { useHandleSuccessCreation } from "app/pages/activation/hooks/topics/modal/useHandleSuccessCreation";
import { activationTracking } from "app/pages/activation/services/tracking";
import { translate } from "app/filters/i18nFilter";
import { FileInput } from "app/pages/activation/components/Shared/FileInput/FileInput";
import useAdvancedKWModal from "app/pages/activation/hooks/advancedKWModal/useAdvancedKWModal";

import { generateAutomationName } from "../../../../../helpers/generateAutomationName";
import { MODAL_TEXTS } from "../../../texts";
import { CreationFlow } from "../../../types";
import { MAX_TOPIC_NAME_LENGTH } from "../Collision/constants";

import { LIST_CONFIG, TOOLTIP_WIDTH } from "./constants";
import Input from "./Input/Input";
import KeywordItem from "./KeywordItem/KeywordItem";
import KeywordsDropdown from "./KeywordsDropdown/KeywordsDropdown";
import * as SC from "./styles";
import { keywordModalTexts } from "./texts";

//TODO refactor
export const Advanced = () => {
    const { onClose, onAdvanceMenuClickBack } = useTopicsModalContext();

    const { onSort, activationId, granularity } = useTopicsContext();

    const {
        selectedKeywords,
        keywordsList,
        queryKeyword,
        isIncludedInList,
        title,
        titleError,
        existingKWNames,
        keywordsGroups,
        savedKeywords,
        isParsing,
        onDropdownItemSelect,
        selectKWFromSearch,
        clearSelection,
        onKeyPress,
        onPaste,
        onRemove,
        onSelect,
        onChange,
        onAdd,
        onChangeTitle,
        setQueryKeyword,
        setKeywordList,
        onFileInput,
    } = useAdvancedKWModal();

    const keywordsLimit = ActivationComponentAssets.maxAmountOfKeywords;
    const handleSuccessCreation = useHandleSuccessCreation({ onClose, onSort });
    const track = activationTracking.home;

    const { isLoading, onCreateWithKeywords, onCancel } = useCreateFlows({
        activationId,
        granularity,
        track,
        handleSuccessCreation,
    });

    const keywordsInputRef = useRef(null);

    const getKeywordList = ({ index, style, key }) => {
        const currentKey = keywordsList[index];

        return (
            <KeywordItem
                onRemove={onRemove}
                onSelect={onSelect}
                currentKey={currentKey}
                style={style}
                key={key}
                index={index}
                query={queryKeyword}
            />
        );
    };

    const onCreateTopic = () => {
        const keywords = {
            name: title,
            keywords: existingKWNames,
        };

        onCreateWithKeywords([keywords]);
    };

    const clearQuery = () => {
        setQueryKeyword("");
        setKeywordList(savedKeywords.current);
    };

    const titleErrorState = !!titleError && !title;

    const selectedText =
        selectedKeywords.current.length === 1
            ? translate(keywordModalTexts.oneKeywordSelected)
            : translate(keywordModalTexts.multiKeywordSelected);

    const isCreateDisabled = !keywordsList.length || titleErrorState || !!titleError;

    return (
        <>
            <SC.AdvancedWrapper
                data-automation={generateAutomationName("home", ["topics", "modal", "advanced", "modal", "body"])}
                id="dropdownAdvanced"
            >
                <AutosizeInput
                    maxLength={MAX_TOPIC_NAME_LENGTH}
                    placeholder={translate(keywordModalTexts.nameYourTopic)}
                    onChange={onChangeTitle}
                    value={title}
                    error={titleErrorState}
                    autoFocus
                    className={classNames(!!titleError && "title-error")}
                    iconName={titleError ? "error-info" : "edit-icon"}
                />

                {titleError ? <SC.TitleError>{titleError}</SC.TitleError> : null}

                <div>
                    <SC.SubTitleWrapper
                        data-automation={generateAutomationName("home", [
                            "topics",
                            "modal",
                            "advanced",
                            "subtitle",
                            "wrapper",
                        ])}
                    >
                        <SC.Subtitle>{translate(keywordModalTexts.subtitle)}</SC.Subtitle>
                        <SC.SubtitleCount>
                            <strong>{savedKeywords.current.length}</strong> /
                            {translate(keywordModalTexts.maxKeywords, {
                                count: keywordsLimit,
                            })}
                        </SC.SubtitleCount>
                    </SC.SubTitleWrapper>
                    <SC.ListContainer>
                        <Input
                            onChange={onChange}
                            onKeyPress={onKeyPress}
                            onPaste={onPaste}
                            onRemove={onRemove}
                            clearSelection={clearSelection}
                            selectKWFromSearch={selectKWFromSearch}
                            onAdd={onAdd}
                            keywordsInputRef={keywordsInputRef}
                            selectedKeywordsCount={selectedKeywords.current.length}
                            selectedText={selectedText}
                            queryKeyword={queryKeyword}
                            isIncludedInList={isIncludedInList}
                            keywordsCount={keywordsList.length}
                            savedKeywords={savedKeywords.current}
                            clearQuery={clearQuery}
                        />
                        <div
                            data-automation={generateAutomationName("home", [
                                "topics",
                                "modal",
                                "advanced",
                                "modal",
                                "list",
                            ])}
                        >
                            {isParsing ? (
                                <SC.LoaderContainer>
                                    <ListContentLoader />
                                    <SC.LoadingText>{translate(keywordModalTexts.importCSVloading)}</SC.LoadingText>
                                </SC.LoaderContainer>
                            ) : (
                                <List
                                    width={LIST_CONFIG.width}
                                    rowHeight={LIST_CONFIG.rowHeight}
                                    height={queryKeyword.length ? LIST_CONFIG.heightSearchState : LIST_CONFIG.height}
                                    rowCount={keywordsList.length}
                                    rowRenderer={getKeywordList}
                                />
                            )}

                            <SC.SelectExistingWrapper>
                                <FileInput
                                    tooltip={keywordModalTexts.importCSVTooltip}
                                    isLoading={isParsing}
                                    onClick={onFileInput}
                                    color={colorsPalettes.blue[500]}
                                    text={keywordModalTexts.importCSV}
                                />

                                <KeywordsDropdown
                                    onDropdownItemSelect={onDropdownItemSelect}
                                    keywordsGroups={keywordsGroups}
                                />
                            </SC.SelectExistingWrapper>
                        </div>
                    </SC.ListContainer>
                </div>
            </SC.AdvancedWrapper>

            <SC.FooterButtons>
                <Button
                    type="flatDark"
                    onClick={onAdvanceMenuClickBack}
                    dataAutomation={generateAutomationName("home", ["topics", "modal", "advanced", "back", "button"])}
                >
                    {translate(MODAL_TEXTS.buttonBack)}
                </Button>

                <FlexRow>
                    <Button
                        type="flat"
                        onClick={onCancel(CreationFlow.Manual)}
                        dataAutomation={generateAutomationName("home", [
                            "topics",
                            "modal",
                            "advanced",
                            "cancel",
                            "button",
                        ])}
                    >
                        {translate(MODAL_TEXTS.buttonCancel)}
                    </Button>
                    <PlainTooltip
                        maxWidth={TOOLTIP_WIDTH}
                        tooltipContent={translate(keywordModalTexts.createButtonTooltip)}
                        enabled={isCreateDisabled}
                    >
                        <SC.TooltipContentWrapper>
                            <SC.StyledButton
                                onClick={onCreateTopic}
                                isLoading={isLoading}
                                isDisabled={isCreateDisabled || isLoading}
                                dataAutomation={generateAutomationName("home", [
                                    "topics",
                                    "modal",
                                    "advanced",
                                    "confirm",
                                    "button",
                                ])}
                            >
                                {translate(MODAL_TEXTS.buttonConfirm)}
                            </SC.StyledButton>
                        </SC.TooltipContentWrapper>
                    </PlainTooltip>
                </FlexRow>
            </SC.FooterButtons>
        </>
    );
};

Advanced.displayName = "TopicModalAdvancedFlow";
